import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button, Modal, Drawer } from 'antd';
import {AcceptButtonWrapper, Content, InlineTextWrapper, LinkText, Wrapper} from './styles';
import { postAPI } from '../../utils/api';
import { GET_FLIGHT_FARE_RULES } from '../../utils/constants';
import {
  useFlightSearchStore,
} from '../../store/flightSearch/flightSearchStore';
import {
  StyledParagraph,
  StyledTitle,
} from '../CustomAntdComponents/Typography';
import { ReactComponent as InfoIcon} from "../../assets/images/info.svg";
import FareContent from "./TermsContent";
import useIsMobile from "../../hooks/useIsMobile";
import TermsAndConditionsContent from './TermsAndConditionsContent';

const defaultFareRules: TermsAndConditions = {
  TC: `
      <div style="font-family: Arial, sans-serif; color:#111827; line-height:1.6; padding:1rem 1.25rem; max-height:70vh; overflow:auto; background:#ffffff; border-radius:12px;">
      <h2 style="margin:0 0 1rem 0; font-size:1.125rem; font-weight:700;">ONLINE TERMS AND CONDITIONS</h2>

      <p>These Terms and Conditions (“Terms”) govern your use of our ticketing platform (“Platform”) for booking air flight and/or road transportation services. By using the Platform, you agree to be bound by these Terms.</p>

      <p>All bookings/reservations made through this Platform are subject to the rules and terms of carriage of our third-party providers, such as airlines and bus companies ("Transport Providers").</p>

      <p>This Platform merely acts as an aggregator and reseller that facilitates the booking of transport services offered by Transport Providers. This Platform SHALL have NO responsibility, whatsoever, for any additional cost (directly or indirectly) incurred by you or any passenger due to any delay, loss, cancellation, change, inaccurate/insufficient information arising whether during booking reservation or after ticket issuance.</p>

      <p>All post-ticketing matters, including but not limited to flight/bus cancellations, rescheduling, boarding issues, delays, baggage handling, or refund requests, shall be addressed directly to the Transport Provider. All refund settlements and change requests, where applicable, are subject to the Transport Provider's policy. All tickets are non-transferable at any time. Some tickets may be non-refundable or non-changeable, subject to the Transport Provider’s policy. Any refund settlement shall be pursuant to the Transport Provider’s confirmation and remittance of funds.</p>

      <p>For air ticket bookings / reservations made via the Platform, please ensure that your international passport has at least 6 (six) months' validity prior to its expiration date, as this Platform shall not be liable for any default. Passengers are liable for travel details, compliance, and adequacy of visa requirements, travel itinerary, and names (as they appear on the passport) provided for bookings.</p>

      <p>Our maximum liability arising from the service provided to you and any passenger shall not exceed the aggregate amount of the ticket purchased and the Platform’s service charge.</p>

      <p>By proceeding to use this Platform, you confirm that you have read and understood our Terms & Conditions and agree to hold us harmless and absolve us any liability arising from the terms set out in our Terms and Conditions of Service.</p>
    </div>
  `,
};

type Props = {
  inline?: boolean;
}

function ConditionsRule({ inline }: Props) {
  const {
    selectedFlight,
     updateFormData
  } = useFlightSearchStore();

  const { isMobile } = useIsMobile();
  const modalContainerRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [loadingTermsData, setLoadingTermsData] = useState<boolean>(true);
  const [termsError, setTermsError] = useState<string| null>(null);
  const [terms, setTerms] = useState<TermsAndConditions | null>(null);

  const getFlightRues = useCallback(async () => {
    setLoadingTermsData(true);
    setTermsError(null);

    if (selectedFlight) {
      const activeFlight = selectedFlight[0];
      const flightRules = await postAPI(GET_FLIGHT_FARE_RULES, {
        AgentId: activeFlight.AgentId,
        GDSId: activeFlight.GDSId,
        CombinationID: activeFlight.Properties.CombinationID,
        RecommendationID: activeFlight.Properties.RecommendationID,
        PassengerTypeCode: 'ADT',
        SessionId: activeFlight.Properties.TripsSessionId,
      });

      setLoadingTermsData(false);

      if (flightRules.status === 200) {
        const rules = flightRules.data.FlightRules.length === 0 ? defaultFareRules : flightRules.data.TermsAndConditions;
        setTerms(rules);
      } else {
        setTerms(defaultFareRules);
      }
    }
  }, [selectedFlight]);

  const handleAccept = () => {
    updateFormData({
      fareRuleAccepted: true,
    });

    setVisible(false);
  }

  const AcceptButton = (<AcceptButtonWrapper>
    <Button block type="primary" onClick={handleAccept}> I Accept </Button>
  </AcceptButtonWrapper>);

  useEffect(() => {
    getFlightRues();
  }, []);

  return (
      <Wrapper ref={modalContainerRef} inline={inline}>
        {
            inline ? (<InlineTextWrapper onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              setVisible(true)
            }}> Terms & Condition
            </InlineTextWrapper>) : (
                <Content onClick={() => setVisible(true)}>
                <StyledParagraph color="primary">
                  Read fare rules
                </StyledParagraph>
                <LinkText>
                  <InfoIcon />
                </LinkText>
            </Content>)
        }

        {/* Mobile display */}
        { isMobile ? (
            <Drawer
                placement="bottom"
                open={visible}
                onClose={() => setVisible(false)}
                footer={null}
                height="100%"
                title="Terms and Conditions"
                getContainer={modalContainerRef.current}
            >
              <TermsAndConditionsContent loadingTermsData={loadingTermsData} terms={terms} termsError={termsError} />
            </Drawer>
        ) : (
            <Modal
                title={<StyledTitle level={4}>Terms and Conditions</StyledTitle>}
                open={visible}
                onCancel={() => setVisible(false)}
                getContainer={modalContainerRef.current}
            footer={null}
                width={750}
            >
              <TermsAndConditionsContent loadingTermsData={loadingTermsData} terms={terms} termsError={termsError} />
            </Modal>
        )}
      </Wrapper>
  );
}

export default ConditionsRule;
